'use client';

import { c as _c } from "react/compiler-runtime";
import { Description, Field, Label } from '@algoacademy/design-system/web/forms/Fieldset';
import { Input } from '@algoacademy/design-system/web/forms/Input';
import { Switch, SwitchField } from '@algoacademy/design-system/web/forms/Switch';
import { type ChangeEvent, type FocusEvent, useState } from 'react';
import { MAXIMUM_DISCOUNT_PERIOD_COUNT, MINIMUM_DISCOUNT_PERIOD_COUNT, parseLiveDiscountFieldValue, readClampedDiscountFieldValue } from '../_helpers/discount-draft';
const SWITCH_FIELD_CLASSNAME = 'w-fit gap-3 *:data-[slot=control]:col-start-1 *:data-[slot=label]:col-start-2';
type DiscountDurationFieldProps = {
  /** null means the discount never ends. */
  periodCount: number | null;
  onChange: (periodCount: number | null) => void;
};
type PeriodCountInputProps = {
  value: number;
  onChange: (value: number) => void;
};
function PeriodCountInput(t0) {
  const $ = _c(13);
  const {
    value,
    onChange
  } = t0;
  const [draftText, setDraftText] = useState(null);
  const isEditing = draftText !== null;
  const displayValue = isEditing ? draftText : String(value);
  let t1;
  if ($[0] !== value) {
    t1 = _event => {
      setDraftText(String(value));
    };
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleFocus = t1;
  let t2;
  if ($[2] !== onChange) {
    t2 = event => {
      const raw = event.target.value;
      const parsed = parseLiveDiscountFieldValue(raw, MINIMUM_DISCOUNT_PERIOD_COUNT, MAXIMUM_DISCOUNT_PERIOD_COUNT);
      setDraftText(parsed === null ? raw : String(parsed));
      if (parsed !== null) {
        onChange(parsed);
      }
    };
    $[2] = onChange;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleChange = t2;
  let t3;
  if ($[4] !== draftText || $[5] !== onChange || $[6] !== value) {
    t3 = _event_0 => {
      const raw_0 = draftText ?? String(value);
      if (raw_0.trim() === "") {
        onChange(MINIMUM_DISCOUNT_PERIOD_COUNT);
      } else {
        const clamped = readClampedDiscountFieldValue(raw_0, MINIMUM_DISCOUNT_PERIOD_COUNT, MAXIMUM_DISCOUNT_PERIOD_COUNT);
        if (clamped !== value) {
          onChange(clamped);
        }
      }
      setDraftText(null);
    };
    $[4] = draftText;
    $[5] = onChange;
    $[6] = value;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleBlur = t3;
  let t4;
  if ($[8] !== displayValue || $[9] !== handleBlur || $[10] !== handleChange || $[11] !== handleFocus) {
    t4 = <Input type="number" step={1} value={displayValue} onFocus={handleFocus} onChange={handleChange} onBlur={handleBlur} aria-label={"Liczba okres\xF3w rozliczeniowych"} className="font-medium tabular-nums" />;
    $[8] = displayValue;
    $[9] = handleBlur;
    $[10] = handleChange;
    $[11] = handleFocus;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}

/**
 * How long the discount lasts, always said in billing periods (invoices). One period
 * here is one faktura at the cadence picked in the offer card above.
 */
export function DiscountDurationField(t0) {
  const $ = _c(23);
  const {
    periodCount,
    onChange
  } = t0;
  const isLimited = periodCount !== null;
  const storedPeriodCount = periodCount ?? MINIMUM_DISCOUNT_PERIOD_COUNT;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Label>Czas trwania</Label>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = isLimited ? "Po podanej liczbie okres\xF3w wraca cena regularna" : "Obowi\u0105zuje przez ca\u0142\u0105 subskrypcj\u0119";
  let t3;
  if ($[1] !== t2) {
    t3 = <Description className="text-contentTertiary leading-5!">{t2}</Description>;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      height: "2.25rem"
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== onChange || $[5] !== storedPeriodCount) {
    t5 = checked => onChange(checked ? storedPeriodCount : null);
    $[4] = onChange;
    $[5] = storedPeriodCount;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== isLimited || $[8] !== t5) {
    t6 = <Switch checked={isLimited} onChange={t5} aria-label={"Ogranicz liczb\u0105 okres\xF3w rozliczeniowych"} />;
    $[7] = isLimited;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Label className="text-nowrap font-normal">Limit okresów</Label>;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t6) {
    t8 = <SwitchField className={SWITCH_FIELD_CLASSNAME}>{t6}{t7}</SwitchField>;
    $[11] = t6;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== isLimited || $[14] !== onChange || $[15] !== storedPeriodCount) {
    t9 = isLimited ? <PeriodCountInput value={storedPeriodCount} onChange={next => onChange(next)} /> : null;
    $[13] = isLimited;
    $[14] = onChange;
    $[15] = storedPeriodCount;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t8 || $[18] !== t9) {
    t10 = <div data-slot="control" className="flex items-center gap-x-4 gap-y-2" style={t4}>{t8}{t9}</div>;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10 || $[21] !== t3) {
    t11 = <Field className="flex-1">{t1}{t3}{t10}</Field>;
    $[20] = t10;
    $[21] = t3;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  return t11;
}